import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { fetchLmsEmployees } from "../../redux/erpSlice";
import { getSessionTeachers } from "../../redux/sessionSlice";

const SessionModal = ({ isOpen, onClose, onSubmit, session }) => {
  const dispatch = useDispatch();
  const { lmsEmployees, loading: employeesLoading } = useSelector((state) => state.lmsEmployees);
  const { sessionTeachers, status: sessionTeachersStatus } = useSelector((state) => state.sessions);
  
  const { register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      sessionName: "",
      start: "",
      end: "",
      status: "Running",
      teacherEmails: []
    }
  });

  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const watchedTeacherEmails = watch("teacherEmails");

  useEffect(() => {
    if (isOpen) {
      // Fetch employees when modal opens
      dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: 100 }));
      
      if (session && typeof session === 'object' && session.id) {
        // If editing, populate form with session data
        setValue("sessionName", session.sessionName || "");
        
        // Safely handle date values with additional validation
        const startDate = session.start && typeof session.start === 'string' && session.start.includes('T')
          ? session.start.split("T")[0] 
          : session.start && typeof session.start === 'string'
          ? session.start
          : "";
        const endDate = session.end && typeof session.end === 'string' && session.end.includes('T')
          ? session.end.split("T")[0] 
          : session.end && typeof session.end === 'string'
          ? session.end
          : "";
          
        setValue("start", startDate);
        setValue("end", endDate);
        setValue("status", session.status || "Running");
        
        // Fetch session teachers for editing
        dispatch(getSessionTeachers(session.id));
        
        // Reset selected teachers initially
        setSelectedTeachers([]);
        setValue("teacherEmails", []);
      } else {
        // Reset form for new session
        reset();
        setSelectedTeachers([]);
      }
    }
  }, [isOpen, session, setValue, reset, dispatch]);

  // Effect to populate selected teachers when session teachers are loaded
  useEffect(() => {
    if (session && session.id && sessionTeachers[session.id]) {
      const teachers = sessionTeachers[session.id];
      const teacherEmails = teachers.map(teacher => teacher.teacherEmail);
      setSelectedTeachers(teacherEmails);
      setValue("teacherEmails", teacherEmails);
    }
  }, [sessionTeachers, session, setValue]);

  const handleTeacherToggle = (teacherEmail) => {
    const newSelectedTeachers = selectedTeachers.includes(teacherEmail)
      ? selectedTeachers.filter(email => email !== teacherEmail)
      : [...selectedTeachers, teacherEmail];
    
    setSelectedTeachers(newSelectedTeachers);
    setValue("teacherEmails", newSelectedTeachers);
  };

  const handleFormSubmit = (data) => {
    const sessionData = {
      sessionData: {
        sessionName: data.sessionName,
        start: data.start,
        end: data.end,
        status: data.status
      },
      teacherEmails: data.teacherEmails
    };
    
    onSubmit(sessionData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-medium">
            {session ? "Edit Session" : "Add New Session"}
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4 space-y-4">
          {/* Session Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Session Name *</label>
            <input
              type="text"
              {...register("sessionName", { required: "Session name is required" })}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
              placeholder="Enter session name"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium mb-1">Start Date *</label>
            <input
              type="date"
              {...register("start", { required: "Start date is required" })}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium mb-1">End Date *</label>
            <input
              type="date"
              {...register("end", { required: "End date is required" })}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium mb-1">Status *</label>
            <select
              {...register("status", { required: "Status is required" })}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
            >
              <option value="Running">Running</option>
              <option value="Completed">Completed</option>
              <option value="Scheduled">Scheduled</option>
            </select>
          </div>

          {/* Teacher Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Select Teachers *</label>
            <div className="border rounded p-3 max-h-60 overflow-y-auto">
              {employeesLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#241763]"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading teachers...</p>
                </div>
              ) : lmsEmployees.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No teachers available</p>
              ) : (
                <div className="space-y-2">
                  {lmsEmployees.map((teacher) => (
                    <div key={teacher.email} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`teacher-${teacher.email}`}
                        checked={selectedTeachers.includes(teacher.email)}
                        onChange={() => handleTeacherToggle(teacher.email)}
                        className="rounded border-gray-300 text-[#241763] focus:ring-[#241763]"
                      />
                      <label 
                        htmlFor={`teacher-${teacher.email}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {teacher.firstName} {teacher.lastName} ({teacher.email})
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedTeachers.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  Selected teachers: {selectedTeachers.length}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedTeachers.map((email) => (
                    <span 
                      key={email}
                      className="bg-[#241763] text-white text-xs px-2 py-1 rounded"
                    >
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#241763] text-white rounded hover:bg-[#1a0f4a]"
            >
              {session ? "Update Session" : "Create Session"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SessionModal;
